import { Link } from 'react-router-dom';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { useBrand } from '@/hooks/useBrand';
import { useHomepageContent } from '@/hooks/useHomepageContent';
import type { FooterLink } from '@/lib/homepageContent';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const { user } = useAuth();
  const { t } = useTranslation();
  const { brand } = useBrand();
  const { content } = useHomepageContent();
  const footer = content.footer;

  const handleNewsletter = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!email || !email.includes('@')) {
      setError('Veuillez entrer une adresse email valide');
      return;
    }
    try {
      const response = await fetch('https://readdy.ai/api/form/d8q5674jgj4v45gj728g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ email }),
      });
      if (response.ok) {
        setSubmitted(true);
        setEmail('');
      } else {
        setError('Une erreur est survenue. Veuillez réessayer.');
      }
    } catch (_) {
      setError('Une erreur est survenue. Veuillez réessayer.');
    }
  };

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const renderFooterLink = (link: FooterLink, i: number) => {
    if (link.href.startsWith('#')) {
      return (
        <button
          key={i}
          onClick={() => scrollToSection(link.href.slice(1))}
          className="text-sm text-background-50/50 hover:text-background-50 transition-colors cursor-pointer"
        >
          {link.label}
        </button>
      );
    }
    return (
      <a
        key={i}
        href={link.href}
        className="text-sm text-background-50/50 hover:text-background-50 transition-colors cursor-pointer"
        rel="nofollow"
      >
        {link.label}
      </a>
    );
  };

  return (
    <footer className="relative bg-foreground-950 text-background-50">
      <div className="w-full px-4 md:px-6 lg:px-10 max-w-7xl mx-auto py-14 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-8">
          <div className="lg:col-span-4">
            <Link to="/" className="flex items-center gap-2.5 mb-5 no-underline">
              <div className="w-10 h-10 rounded-xl bg-primary-500 flex items-center justify-center overflow-hidden">
                {brand.logo ? (
                  <img src={brand.logo} alt={brand.name || 'ZIFEK'} className="w-full h-full object-contain p-0.5" />
                ) : (
                  <span className="text-background-50 text-xl font-bold font-heading">
                    {(brand.name || 'Z').charAt(0)}
                  </span>
                )}
              </div>
              <span className="text-3xl font-bold font-heading text-background-50">
                {brand.name || 'ZIFEK'}
              </span>
            </Link>
            <p className="text-background-50/50 text-sm leading-relaxed max-w-sm mb-6">
              {t('home.footer.about', { defaultValue: footer.aboutText })}
            </p>
            <div className="flex items-center gap-3">
              {footer.socials.map((social, i) => (
                <a
                  key={i}
                  href={social.url}
                  className="w-9 h-9 rounded-full border border-background-50/15 flex items-center justify-center text-background-50/50 hover:text-background-50 hover:border-background-50/30 transition-colors cursor-pointer"
                  aria-label={social.label}
                  rel="nofollow"
                >
                  <i className={`${social.icon} text-sm`}></i>
                </a>
              ))}
            </div>
          </div>

          {footer.columns.map((column, ci) => (
            <div key={ci} className={`lg:col-span-2 ${ci === 0 ? 'lg:col-start-6' : ''}`}>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-background-50/30 mb-4">
                {column.title}
              </h4>
              <ul className="space-y-2.5">
                {column.links.map((link, i) => (
                  <li key={i}>{renderFooterLink(link, i)}</li>
                ))}
              </ul>
            </div>
          ))}

          <div className="lg:col-span-2">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-background-50/30 mb-4">
              {t('home.footer.account', { defaultValue: 'Compte' })}
            </h4>
            <ul className="space-y-2.5">
              {!user && (
                <>
                  <li>
                    <Link
                      to="/login-client"
                      className="text-sm text-background-50/50 hover:text-background-50 transition-colors cursor-pointer no-underline"
                    >
                      Connexion client
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/register-client"
                      className="text-sm text-background-50/50 hover:text-background-50 transition-colors cursor-pointer no-underline"
                    >
                      Créer un compte
                    </Link>
                  </li>
                </>
              )}
              <li>
                <Link
                  to="/login"
                  className="text-sm text-background-50/50 hover:text-background-50 transition-colors cursor-pointer no-underline"
                >
                  Espace professionnel
                </Link>
              </li>
              <li>
                <Link
                  to="/register"
                  className="text-sm text-background-50/50 hover:text-background-50 transition-colors cursor-pointer no-underline"
                >
                  Devenir marchand
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Bloc SEO « Campagnes créateurs » */}
        <section className="mt-12 rounded-xl border border-background-50/10 bg-background-50/5 p-6 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-5">
          <div className="max-w-2xl">
            <h4 className="text-lg md:text-xl font-bold font-heading text-background-50 mb-2">
              <Link to="/missions-campagnes" className="text-background-50 hover:text-primary-300 transition-colors no-underline">
                Campagnes créateurs
              </Link>
            </h4>
            <p className="text-sm text-background-50/55 leading-relaxed">
              Les commerces Zifek publient des missions de visibilité : partagez leur contenu sur TikTok,
              Instagram, Facebook, YouTube ou X, et soyez rémunéré au nombre de vues. Découvrez les campagnes
              ouvertes, postulez avec vos réseaux sociaux et suivez vos gains depuis votre espace.
            </p>
          </div>
          <Link
            to="/missions-campagnes"
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-background-50 text-foreground-950 text-sm font-semibold whitespace-nowrap hover:bg-background-100 transition-colors no-underline shrink-0"
          >
            Voir les campagnes ouvertes
            <i className="ri-arrow-right-line"></i>
          </Link>
        </section>

        {/* Newsletter + copyright */}
        <div className="mt-12 pt-8 border-t border-background-50/10">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <p className="text-xs text-background-50/30">
                © {new Date().getFullYear()} {t('home.footer.copyright', { defaultValue: footer.copyrightText })}
              </p>
              <div className="flex items-center gap-4">
                <Link
                  to="/confidentialite"
                  className="text-xs text-background-50/30 hover:text-background-50/60 transition-colors cursor-pointer no-underline"
                >
                  {t('home.footer.privacy', { defaultValue: 'Politique de confidentialité' })}
                </Link>
                <Link
                  to="/conditions-utilisation"
                  className="text-xs text-background-50/30 hover:text-background-50/60 transition-colors cursor-pointer no-underline"
                >
                  {t('home.footer.terms', { defaultValue: "Conditions d'utilisation" })}
                </Link>
              </div>
            </div>

            <form
              onSubmit={handleNewsletter}
              data-readdy-form
              className="flex items-center gap-2"
            >
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="absolute w-0 h-0 opacity-0"
              />
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('home.footer.email_ph', { defaultValue: 'Votre email' })}
                required
                className="w-52 h-10 pl-4 pr-4 rounded-full bg-background-50/5 border border-background-50/10 text-sm text-background-50 placeholder:text-background-50/25 focus:outline-none focus:border-background-50/25 transition-colors"
              />
              <button
                type="submit"
                className="px-5 h-10 bg-background-50 text-foreground-950 rounded-full text-xs font-semibold whitespace-nowrap hover:bg-background-100 transition-colors cursor-pointer"
              >
                {submitted ? t('home.footer.subscribed', { defaultValue: 'Inscrit !' }) : t('home.footer.subscribe', { defaultValue: "S'abonner" })}
              </button>
            </form>
          </div>
          {error && (
            <p className="text-xs text-red-400 mt-2">{error}</p>
          )}
        </div>

        {/* Liens éditoriaux (maillage interne) */}
        <nav aria-label="Ressources" className="mt-6 pt-6 border-t border-background-50/10 flex flex-wrap items-center gap-x-5 gap-y-2">
          <Link to="/blog" className="text-xs text-background-50/40 hover:text-background-50 transition-colors no-underline">
            Blog
          </Link>
          <Link to="/documentation" className="text-xs text-background-50/40 hover:text-background-50 transition-colors no-underline">
            Documentation
          </Link>
          <Link to="/api" className="text-xs text-background-50/40 hover:text-background-50 transition-colors no-underline">
            API
          </Link>
          <Link to="/communaute" className="text-xs text-background-50/40 hover:text-background-50 transition-colors no-underline">
            Communauté
          </Link>
          <Link to="/status" className="text-xs text-background-50/40 hover:text-background-50 transition-colors no-underline">
            Statut des services
          </Link>
        </nav>
      </div>
    </footer>
  );
}